import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../api";
import "./estilodash.css";

const ClientesList = () => {
  const [clientes, setClientes] = useState([]);
  const [licencas, setLicencas] = useState([]);
  const [assinaturas, setAssinaturas] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);

  const [busca, setBusca] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);

  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [filtroIsento, setFiltroIsento] = useState("todos");
  const [filtroAssinatura, setFiltroAssinatura] = useState("todos");
  const [filtroTrial, setFiltroTrial] = useState("todos");

  const editRef = useRef(null);

  useEffect(() => {
    carregarTudo();
  }, []);

  useEffect(() => {
    if (clienteSelecionado && editRef.current) {
      editRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [clienteSelecionado]);

  const carregarTudo = async () => {
    try {
      setCarregando(true);
      setErro(null);

      const [respClientes, respLicencas, respAssinaturas] = await Promise.all([
        api.get("/api/clientes"),
        api.get("/api/licencas-v2"),
        api.get("/api/assinaturas"),
      ]);

      setClientes(respClientes.data || []);
      setLicencas(respLicencas.data || []);
      setAssinaturas(respAssinaturas.data || []);

      const filtroId = localStorage.getItem("clienteFiltroId");
      if (filtroId) {
        const cli = (respClientes.data || []).find(
          (c) => String(c.id) === filtroId
        );
        if (cli) {
          setBusca(cli.nome || filtroId);
        }
        localStorage.removeItem("clienteFiltroId");
      }
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      setErro("Erro ao carregar dados. Verifique o login/servidor.");
    } finally {
      setCarregando(false);
    }
  };

  const hojeISO = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const formatarData = (valor) => {
    if (!valor) return "—";
    const data = new Date(valor);
    if (Number.isNaN(data.getTime())) return valor;
    return data.toLocaleDateString("pt-BR");
  };

  const getAssinaturasCliente = (clienteId) => {
    return assinaturas.filter(
      (assinatura) => Number(assinatura.cliente_id) === Number(clienteId)
    );
  };

  const getAssinaturasAtivasCliente = (clienteId) => {
    return getAssinaturasCliente(clienteId).filter(
      (assinatura) =>
        Number(assinatura.assinatura_status ?? assinatura.status ?? 0) === 1
    );
  };

  const getResumoAssinaturas = (clienteId) => {
    const todas = getAssinaturasCliente(clienteId);
    const ativas = getAssinaturasAtivasCliente(clienteId);
    const base = ativas.length ? ativas : todas;

    if (!base.length) {
      return {
        label: "Sem assinatura",
        detalhe: "0 ativa",
        ativas: 0,
        total: 0,
        slotsAtivos: 0,
      };
    }

    const nomes = base.slice(0, 2).map((assinatura) => {
      const nome = assinatura.nome_plano || assinatura.codigo_plano || "Assinatura";
      return `${nome} (${assinatura.tier || "starter"})`;
    });

    const restante = Math.max(base.length - nomes.length, 0);
    const label = `${nomes.join(" + ")}${restante ? ` +${restante}` : ""}`;
    const slotsAtivos = ativas.reduce(
      (total, assinatura) => total + Number(assinatura.slots_contratados || 0),
      0
    );

    return {
      label,
      detalhe: `${ativas.length}/${todas.length} ativas`,
      ativas: ativas.length,
      total: todas.length,
      slotsAtivos,
    };
  };

  const getLicencasCadastradas = (clienteId) => {
    return licencas.filter(
      (lic) => Number(lic.cliente_id || lic.id_cliente) === Number(clienteId)
    ).length;
  };

  const getTrialInfo = (cliente) => {
    if (!cliente?.trial_ate) {
      return {
        possuiTrial: false,
        vencido: false,
        ativo: false,
        label: "Sem teste",
      };
    }

    const hoje = hojeISO();
    const trial = String(cliente.trial_ate).slice(0, 10);

    if (trial < hoje) {
      return {
        possuiTrial: true,
        vencido: true,
        ativo: false,
        label: "Teste vencido",
      };
    }

    return {
      possuiTrial: true,
      vencido: false,
      ativo: true,
      label: "Teste ativo",
    };
  };

  const handleAlterarStatus = async (cliente) => {
    const novoStatus = String(cliente.status) === "1" ? "0" : "1";

    if (
      !window.confirm(
        `Deseja ${novoStatus === "1" ? "ATIVAR" : "DESATIVAR"} o cliente "${cliente.nome}"?`
      )
    ) {
      return;
    }

    try {
      await api.put(`/api/clientes/${cliente.id}/status`, {
        status: novoStatus,
      });
      carregarTudo();
    } catch (error) {
      console.error("Erro ao alterar o status do cliente:", error);
      alert("Erro ao alterar o status do cliente.");
    }
  };

  const handleToggleIsento = async (cliente) => {
    const novoIsento = String(cliente.isento) === "1" ? "0" : "1";

    if (
      !window.confirm(
        `Deseja marcar o cliente "${cliente.nome}" como ${
          novoIsento === "1" ? "ISENTO" : "NÃO ISENTO"
        }?`
      )
    ) {
      return;
    }

    try {
      await api.put(`/api/clientes/${cliente.id}/isento`, {
        isento: novoIsento,
      });
      carregarTudo();
    } catch (error) {
      console.error("Erro ao atualizar isento do cliente:", error);
      alert("Erro ao atualizar isento do cliente.");
    }
  };

  const handleAlterarSlots = async (cliente) => {
    const atual = cliente.slots_contratados ?? 2;

    const entrada = window.prompt(
      `Informe o número de SLOTS contratados para o cliente "${cliente.nome}":`,
      atual
    );

    if (entrada === null) return;

    const slots = parseInt(entrada, 10);
    if (isNaN(slots) || slots < 1) {
      alert("Valor inválido. Informe um número inteiro maior ou igual a 1.");
      return;
    }

    try {
      await api.put(`/api/clientes/${cliente.id}/slots`, {
        slots_contratados: slots,
      });
      carregarTudo();
    } catch (error) {
      console.error("Erro ao atualizar slots contratados do cliente:", error);
      alert("Erro ao atualizar slots contratados.");
    }
  };

  const handleEditarCliente = (cliente) => {
    setClienteSelecionado({
      ...cliente,
      slots_contratados: cliente.slots_contratados ?? "",
      dia_vencimento: cliente.dia_vencimento ?? "",
      trial_ate: cliente.trial_ate
        ? String(cliente.trial_ate).slice(0, 10)
        : "",
    });
  };

  const handleCancelarEdicao = () => {
    setClienteSelecionado(null);
  };

  const handleSalvarEdicao = async () => {
    if (!clienteSelecionado) return;

    let diaVenc = clienteSelecionado.dia_vencimento;

    if (diaVenc !== undefined && diaVenc !== null && diaVenc !== "") {
      diaVenc = parseInt(diaVenc, 10);
      if (isNaN(diaVenc) || diaVenc < 1 || diaVenc > 28) {
        alert("Dia de vencimento inválido. Informe um número entre 1 e 28.");
        return;
      }
    } else {
      diaVenc = undefined;
    }

    let trialAte = clienteSelecionado.trial_ate;
    if (trialAte === "") {
      trialAte = "";
    }

    try {
      await api.put(`/api/clientes/${clienteSelecionado.id}`, {
        nome: clienteSelecionado.nome,
        email: clienteSelecionado.email,
        phone: clienteSelecionado.phone,
        cpf: clienteSelecionado.cpf,
        slots_contratados: clienteSelecionado.slots_contratados,
        dia_vencimento: diaVenc,
        trial_ate: trialAte,
      });

      setClienteSelecionado(null);
      carregarTudo();
    } catch (error) {
      console.error("Erro ao salvar o cliente:", error);
      alert("Erro ao salvar o cliente.");
    }
  };

  const handleRemoverCliente = async (id) => {
    if (!window.confirm("Deseja realmente remover este cliente?")) return;

    try {
      await api.delete(`/api/clientes/${id}`);
      carregarTudo();
    } catch (error) {
      console.error("Erro ao remover o cliente:", error);
      alert("Erro ao remover o cliente.");
    }
  };

  let clientesFiltrados = clientes.filter((cliente) => {
    if (!busca) return true;
    const termo = busca.toLowerCase();
    return (
      String(cliente.id).includes(termo) ||
      (cliente.nome && cliente.nome.toLowerCase().includes(termo)) ||
      (cliente.email && cliente.email.toLowerCase().includes(termo)) ||
      (cliente.cpf && String(cliente.cpf).toLowerCase().includes(termo)) ||
      (cliente.phone && String(cliente.phone).toLowerCase().includes(termo))
    );
  });

  if (filtroStatus === "ativos") {
    clientesFiltrados = clientesFiltrados.filter(
      (c) => String(c.status) === "1"
    );
  } else if (filtroStatus === "inativos") {
    clientesFiltrados = clientesFiltrados.filter(
      (c) => String(c.status) !== "1"
    );
  }

  if (filtroIsento === "isentos") {
    clientesFiltrados = clientesFiltrados.filter(
      (c) => String(c.isento) === "1"
    );
  } else if (filtroIsento === "nao-isentos") {
    clientesFiltrados = clientesFiltrados.filter(
      (c) => String(c.isento) !== "1"
    );
  }

  if (filtroAssinatura === "com-ativa") {
    clientesFiltrados = clientesFiltrados.filter(
      (c) => getAssinaturasAtivasCliente(c.id).length > 0
    );
  } else if (filtroAssinatura === "sem-ativa") {
    clientesFiltrados = clientesFiltrados.filter(
      (c) => getAssinaturasAtivasCliente(c.id).length === 0
    );
  } else if (filtroAssinatura === "sem-assinatura") {
    clientesFiltrados = clientesFiltrados.filter(
      (c) => getAssinaturasCliente(c.id).length === 0
    );
  }

  if (filtroTrial === "ativos") {
    clientesFiltrados = clientesFiltrados.filter((c) => getTrialInfo(c).ativo);
  } else if (filtroTrial === "vencidos") {
    clientesFiltrados = clientesFiltrados.filter((c) => getTrialInfo(c).vencido);
  } else if (filtroTrial === "sem-teste") {
    clientesFiltrados = clientesFiltrados.filter((c) => !getTrialInfo(c).possuiTrial);
  }

  const clientesOrdenados = useMemo(() => {
    return [...clientesFiltrados].sort((a, b) =>
      String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR")
    );
  }, [clientesFiltrados]);

  return (
    <div className="pagamentos-container">
      <h2>Clientes</h2>

      <div className="form-linha" style={{ marginBottom: 12 }}>
        <label>
          Buscar
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Nome, email, CPF, telefone ou ID"
          />
        </label>

        <label>
          Status
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
          >
            <option value="todos">Todos</option>
            <option value="ativos">Ativos</option>
            <option value="inativos">Inativos</option>
          </select>
        </label>

        <label>
          Isenção
          <select
            value={filtroIsento}
            onChange={(e) => setFiltroIsento(e.target.value)}
          >
            <option value="todos">Todos</option>
            <option value="isentos">Isentos</option>
            <option value="nao-isentos">Não isentos</option>
          </select>
        </label>

        <label>
          Assinatura
          <select
            value={filtroAssinatura}
            onChange={(e) => setFiltroAssinatura(e.target.value)}
          >
            <option value="todos">Todas</option>
            <option value="com-ativa">Com assinatura ativa</option>
            <option value="sem-ativa">Sem assinatura ativa</option>
            <option value="sem-assinatura">Sem nenhuma assinatura</option>
          </select>
        </label>

        <label>
          Teste
          <select
            value={filtroTrial}
            onChange={(e) => setFiltroTrial(e.target.value)}
          >
            <option value="todos">Todos</option>
            <option value="ativos">Teste ativo</option>
            <option value="vencidos">Teste vencido</option>
            <option value="sem-teste">Sem teste</option>
          </select>
        </label>
      </div>

      {clienteSelecionado && (
        <div
          ref={editRef}
          style={{
            marginBottom: 18,
            background: "rgba(8, 13, 42, 0.94)",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: 16,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          }}
        >
          <h3>Editar cliente #{clienteSelecionado.id}</h3>

          <div className="form-linha">
            <label>
              Nome
              <input
                value={clienteSelecionado.nome || ""}
                onChange={(e) =>
                  setClienteSelecionado((prev) => ({
                    ...prev,
                    nome: e.target.value,
                  }))
                }
              />
            </label>

            <label>
              E-mail
              <input
                value={clienteSelecionado.email || ""}
                onChange={(e) =>
                  setClienteSelecionado((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
              />
            </label>
          </div>

          <div className="form-linha">
            <label>
              Telefone
              <input
                value={clienteSelecionado.phone || ""}
                onChange={(e) =>
                  setClienteSelecionado((prev) => ({
                    ...prev,
                    phone: e.target.value,
                  }))
                }
              />
            </label>

            <label>
              CPF
              <input
                value={clienteSelecionado.cpf || ""}
                onChange={(e) =>
                  setClienteSelecionado((prev) => ({
                    ...prev,
                    cpf: e.target.value,
                  }))
                }
              />
            </label>
          </div>

          <div className="form-linha">
            <label>
              Slots contratados
              <input
                type="number"
                min="1"
                value={clienteSelecionado.slots_contratados || ""}
                onChange={(e) =>
                  setClienteSelecionado((prev) => ({
                    ...prev,
                    slots_contratados: e.target.value,
                  }))
                }
              />
            </label>

            <label>
              Dia de vencimento
              <input
                type="number"
                min="1"
                max="28"
                value={clienteSelecionado.dia_vencimento || ""}
                onChange={(e) =>
                  setClienteSelecionado((prev) => ({
                    ...prev,
                    dia_vencimento: e.target.value,
                  }))
                }
              />
            </label>

            <label>
              Teste até
              <input
                type="date"
                value={clienteSelecionado.trial_ate || ""}
                onChange={(e) =>
                  setClienteSelecionado((prev) => ({
                    ...prev,
                    trial_ate: e.target.value,
                  }))
                }
              />
            </label>
          </div>

          <div className="barra-acoes-pagamento">
            <button type="button" className="qt-btn qt-btn-primary" onClick={handleSalvarEdicao}>
              Salvar alterações
            </button>
            <button type="button" className="qt-btn qt-btn-neutral" onClick={handleCancelarEdicao}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {erro ? (
        <div className="msg-erro">{erro}</div>
      ) : carregando ? (
        <p>Carregando clientes...</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="clientes-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Telefone</th>
                <th>CPF</th>
                <th>Status</th>
                <th>Isento</th>
                <th>Assinaturas</th>
                <th>Slots ativos</th>
                <th>Ass. ativas</th>
                <th>Licenças</th>
                <th>Vencimento</th>
                <th>Teste até</th>
                <th>Trial</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {clientesOrdenados.map((cliente) => {
                const resumoAssinaturas = getResumoAssinaturas(cliente.id);
                const qtdLicencas = getLicencasCadastradas(cliente.id);
                const ativo = String(cliente.status) === "1";
                const isento = String(cliente.isento) === "1";
                const trialInfo = getTrialInfo(cliente);

                return (
                  <tr key={cliente.id} className={!ativo ? "linha-inativa" : ""}>
                    <td>{cliente.id}</td>
                    <td>{cliente.nome}</td>
                    <td>{cliente.email}</td>
                    <td>{cliente.phone || "—"}</td>
                    <td>{cliente.cpf || "—"}</td>
                    <td>
                      {ativo ? (
                        <span className="qt-badge qt-badge-success">Ativo</span>
                      ) : (
                        <span className="qt-badge qt-badge-danger">Inativo</span>
                      )}
                    </td>
                    <td>
                      {isento ? (
                        <span className="qt-badge qt-badge-info">Isento</span>
                      ) : (
                        <span className="qt-badge qt-badge-neutral">Normal</span>
                      )}
                    </td>
                    <td style={{ textAlign: "left", minWidth: 220 }}>
                      <strong>{resumoAssinaturas.label}</strong>
                      <br />
                      <small>{resumoAssinaturas.detalhe}</small>
                    </td>
                    <td>{resumoAssinaturas.slotsAtivos}</td>
                    <td>
                      {resumoAssinaturas.ativas}/{resumoAssinaturas.total}
                    </td>
                    <td>{qtdLicencas}</td>
                    <td>{cliente.dia_vencimento || "—"}</td>
                    <td>{formatarData(cliente.trial_ate)}</td>
                    <td>
                      {!trialInfo.possuiTrial ? (
                        <span className="qt-badge qt-badge-neutral">Sem teste</span>
                      ) : trialInfo.vencido ? (
                        <span className="qt-badge qt-badge-danger qt-badge-trial-expired">
                          Teste vencido
                        </span>
                      ) : (
                        <span className="qt-badge qt-badge-warning">
                          Teste ativo
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="cliente-acoes">
                        <button
                          type="button"
                          className="qt-btn qt-btn-warning cliente-btn cliente-btn-editar"
                          onClick={() => handleEditarCliente(cliente)}
                        >
                          Editar
                        </button>

                        <button
                          type="button"
                          className="qt-btn qt-btn-secondary cliente-btn cliente-btn-slots"
                          onClick={() => handleAlterarSlots(cliente)}
                        >
                          Slots
                        </button>

                        <button
                          type="button"
                          className="qt-btn qt-btn-info cliente-btn cliente-btn-isento"
                          onClick={() => handleToggleIsento(cliente)}
                        >
                          {isento ? "Tirar isenção" : "Isentar"}
                        </button>

                        <button
                          type="button"
                          className={`qt-btn cliente-btn ${
                            ativo
                              ? "qt-btn-danger cliente-btn-desativar"
                              : "qt-btn-success cliente-btn-ativar"
                          }`}
                          onClick={() => handleAlterarStatus(cliente)}
                        >
                          {ativo ? "Desativar" : "Ativar"}
                        </button>

                        <button
                          type="button"
                          className="qt-btn qt-btn-danger cliente-btn cliente-btn-remover"
                          onClick={() => handleRemoverCliente(cliente.id)}
                        >
                          Remover
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {clientesOrdenados.length === 0 && (
                <tr>
                  <td colSpan="15">Nenhum cliente encontrado.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ClientesList;
